import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  getBusinessWalletSummary,
} from '../../lib/api';
import {
  formatCurrency,
} from '../../lib/utils';
import {
  LayoutDashboard, 
  MapPin, 
  Zap, 
  Users, 
  UserCircle,
  Shield, 
  CreditCard, 
  Wallet, 
  Receipt, 
  FileText,
  FileCheck,
  BarChart3, 
  Settings, 
  ScrollText, 
  Activity,
  TrendingUp,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Banknote,
  NfcIcon,
  Globe,
  DollarSign,
  Bell,
  Plug,
  FlaskConical,
  Server,
  Database,
  Building2,
  Lock,
  Monitor,
  Play,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { canUseModeratorManualCharge } from '../../lib/manualChargeAccess';

// Submenu items for Konfigurime
const settingsSubItems = [
  { to: '/settings/system', label: 'Sistemi', icon: Globe },
  { to: '/settings/pricing', label: 'Çmimet', icon: DollarSign },
  { to: '/settings/payments', label: 'Pagesat', icon: CreditCard },
  { to: '/settings/ocpp', label: 'OCPP', icon: Zap },
  { to: '/settings/connectors', label: 'Konektorët', icon: Plug },
  { to: '/settings/notifications', label: 'Njoftimet', icon: Bell },
  { to: '/settings/integrations', label: 'Integrime', icon: MapPin },
  { to: '/backup', label: 'Backup', icon: Database },
  { to: '/audit-logs', label: 'Logs', icon: ScrollText },
];

const navItems = {
  ADMIN: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Paneli' },
    { to: '/sites', icon: MapPin, label: 'Stacionet' },
    { to: '/chargers', icon: Zap, label: 'Karikuesit' },
    { to: '/live', icon: Monitor, label: 'Statusi Live' },
    { to: '/sessions', icon: Activity, label: 'Sesionet' },
    { to: '/ocpp-logs', icon: Server, label: 'OCPP Logs' },
    { divider: true, label: 'Menaxhimi' },
    { to: '/staff', icon: Users, label: 'Përdoruesit' },
    { to: '/clients', icon: UserCircle, label: 'Klientët' },
    { to: '/rfid-cards', icon: NfcIcon, label: 'Kartat RFID' },
    { to: '/moderators', icon: Shield, label: 'Moderatorët' },
    { to: '/admin-businesses', icon: Building2, label: 'Bizneset' },
    { divider: true, label: 'Financat' },
    { to: '/payment-processors', icon: Banknote, label: 'Pagesat' },
    { to: '/cash-topup', icon: Receipt, label: 'Mbushje Cash' },
    { to: '/invoices', icon: FileText, label: 'Faturat' },
    { to: '/fiscal-invoices', icon: FileCheck, label: 'Faturat Fiskale' },
    { divider: true, label: 'Komunikim' },
    { to: '/push-notifications', icon: Bell, label: 'Push Notifications' },
    { divider: true, label: 'Raporte' },
    { to: '/analytics', icon: TrendingUp, label: 'Analiza' },
    { to: '/reports', icon: BarChart3, label: 'Raportet' },
    { to: '/change-password', icon: Lock, label: 'Ndrysho Fjalëkalimin' },
    { divider: true },
    { submenu: true, icon: Settings, label: 'Konfigurime', items: settingsSubItems },
  ],
  MODERATOR: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Paneli' },
    { to: '/sites', icon: MapPin, label: 'Stacionet' },
    { to: '/chargers', icon: Zap, label: 'Karikuesit' },
    { to: '/live', icon: Monitor, label: 'Statusi Live' },
    { to: '/sessions', icon: Activity, label: 'Sesionet' },
    { divider: true, label: 'Klientët' },
    { to: '/clients', icon: UserCircle, label: 'Klientët' },
    { to: '/rfid-cards', icon: NfcIcon, label: 'Kartat RFID' },
    { divider: true },
    { to: '/cash-topup', icon: Receipt, label: 'Mbushje Cash' },
    { to: '/analytics', icon: TrendingUp, label: 'Analiza' },
    { to: '/reports', icon: BarChart3, label: 'Raportet' },
    { to: '/change-password', icon: Lock, label: 'Ndrysho Fjalëkalimin' },
  ],
  USER: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Paneli' },
    { to: '/wallet', icon: Wallet, label: 'Portofoli Im' },
    { to: '/sessions', icon: Activity, label: 'Sesionet e Mia' },
    { to: '/change-password', icon: Lock, label: 'Ndrysho Fjalëkalimin' },
  ],
  BUSINESS: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Paneli' },
    { to: '/sites', icon: MapPin, label: 'Stacionet' },
    { to: '/chargers', icon: Zap, label: 'Karikuesit' },
    { to: '/sessions', icon: Activity, label: 'Sesionet' },
    { divider: true, label: 'Punonjësit' },
    { to: '/clients', icon: UserCircle, label: 'Punonjësit' },
    { to: '/rfid-cards', icon: NfcIcon, label: 'Kartat RFID' },
    { divider: true },
    { to: '/cash-topup', icon: Receipt, label: 'Mbushje Cash' },
    { to: '/invoices', icon: FileText, label: 'Faturat' },
    { to: '/analytics', icon: TrendingUp, label: 'Analiza' },
    { to: '/reports', icon: BarChart3, label: 'Raportet' },
    { to: '/change-password', icon: Lock, label: 'Ndrysho Fjalëkalimin' },
  ]
};

export const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedRoleRaw = String(user?.role || '').trim().toUpperCase();
  const resolvedRole = resolvedRoleRaw === 'SUPER_ADMIN' ? 'ADMIN' : resolvedRoleRaw;
  const baseItems = [...(navItems[resolvedRole] || navItems.USER)];
  const isBusiness = resolvedRole === 'BUSINESS';
  const [businessBalance, setBusinessBalance] = useState(null);
  const items = (() => {
    if (resolvedRole !== 'MODERATOR') return baseItems;

    let nextItems = [...baseItems];

    if (canUseModeratorManualCharge(user)) {
      nextItems = nextItems.filter(
        (item) => item.to !== '/clients' && !(item.divider && item.label === 'Klientët')
      );

      const already = nextItems.some((item) => item.to === '/manual-charge');
      if (!already) {
        const cashIdx = nextItems.findIndex((item) => item.to === '/cash-topup');
        const manualItem = { to: '/manual-charge', icon: Play, label: 'Karikim Manual' };
        if (cashIdx >= 0) nextItems.splice(cashIdx, 0, manualItem);
        else nextItems.push(manualItem);
      }
    }

    const hasAnalytics = nextItems.some(item => item.to === '/analytics');
    if (hasAnalytics) return nextItems;

    const analyticsItem = { to: '/analytics', icon: TrendingUp, label: 'Analiza' };
    const reportsIndex = nextItems.findIndex(item => item.to === '/reports');
    if (reportsIndex >= 0) {
      nextItems.splice(reportsIndex, 0, analyticsItem);
      return nextItems;
    }

    return [...nextItems, analyticsItem];
  })();
  
  // Track open submenus
  const [openSubmenus, setOpenSubmenus] = useState({});
  
  // Auto-open submenu if current path matches
  useEffect(() => {
    items.forEach(item => {
      if (item.submenu && item.items) {
        const isActive = item.items.some(sub => location.pathname.startsWith(sub.to));
        if (isActive) {
          setOpenSubmenus(prev => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  // Close mobile menu on navigation
  useEffect(() => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadBusinessBalance = async () => {
      if (!isBusiness) {
        setBusinessBalance(null);
        return;
      }
      try {
        const response = await getBusinessWalletSummary();
        setBusinessBalance(Number(response?.data?.business_wallet?.balance || 0));
      } catch (error) {
        console.error('Failed to load business balance for sidebar:', error);
      }
    };

    loadBusinessBalance();

    if (!isBusiness) return;

    const intervalId = window.setInterval(() => {
      loadBusinessBalance();
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isBusiness, location.pathname]);
  
  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => ({ ...prev, [label]: !prev[label] }));
  };
  
  // Select logo based on theme
  const logoSrc = theme === 'dark' ? '/logo-dark.png' : '/logo-light.png';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    // Close mobile menu on navigation
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const renderNavItems = () => (
    <div className="space-y-1">
      {items.map((item, index) => {
        // Divider
        if (item.divider) {
          return (
            <div key={`divider-${index}`} className={cn("pt-4 pb-2", collapsed && !mobileOpen && "pt-2 pb-1")}>
              {(!collapsed || mobileOpen) && item.label && (
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3">
                  {item.label}
                </span>
              )}
              {collapsed && !mobileOpen && <div className="border-t border-border mx-2" />}
            </div>
          );
        }
        
        // Submenu item
        if (item.submenu) {
          const isOpen = openSubmenus[item.label];
          const isActive = item.items?.some(sub => location.pathname.startsWith(sub.to));
          
          return (
            <div key={item.label}>
              <button
                onClick={() => toggleSubmenu(item.label)}
                className={cn(
                  'sidebar-link w-full justify-between',
                  isActive && 'text-[#afd039]'
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {(!collapsed || mobileOpen) && <span>{item.label}</span>}
                </div>
                {(!collapsed || mobileOpen) && (
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform duration-200",
                    isOpen && "rotate-180"
                  )} />
                )}
              </button>
              
              {/* Submenu items */}
              {(!collapsed || mobileOpen) && isOpen && (
                <div className="ml-4 pl-4 border-l border-zinc-800 space-y-1 mt-1">
                  {item.items.map(subItem => (
                    <NavLink
                      key={subItem.to}
                      to={subItem.to}
                      onClick={handleNavClick}
                      className={({ isActive }) => cn(
                        'sidebar-link text-sm py-2',
                        isActive && 'active'
                      )}
                      data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <subItem.icon className="w-4 h-4 flex-shrink-0" />
                      <span>{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        }
        
        // Regular nav item
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={handleNavClick}
            className={({ isActive }) => cn('sidebar-link', isActive && 'active')}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {(!collapsed || mobileOpen) && <span>{item.label}</span>}
          </NavLink>
        );
      })}
    </div>
  );

  // Mobile Overlay
  const MobileOverlay = () => (
    <div 
      className={cn(
        "fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300",
        mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
      onClick={() => setMobileOpen(false)}
    />
  );

  // Mobile Sidebar
  const MobileSidebar = () => (
    <aside className={cn(
      "fixed inset-y-0 left-0 z-50 w-72 border-r border-border transform transition-transform duration-300 ease-in-out lg:hidden",
      mobileOpen ? "translate-x-0" : "-translate-x-full"
    )} style={{ backgroundColor: 'var(--sidebar-bg)' }}>
      {/* Mobile Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <img src={logoSrc} alt="AlbMove" className="h-8 w-auto" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Mobile Nav */}
      <nav className="flex-1 overflow-y-auto p-4">
        {renderNavItems()}
      </nav>

      {/* Mobile User Section */}
      <div className="p-4 border-t border-border">
        {isBusiness && (
          <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
            <p className="text-xs text-zinc-300">Balanca në dispozicion</p>
            <p className="text-base font-semibold text-emerald-400">
              {formatCurrency(businessBalance || 0)}
            </p>
          </div>
        )}
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Dilni
        </Button>
      </div>
    </aside>
  );

  return (
    <>
      {/* Mobile Overlay & Sidebar */}
      <MobileOverlay />
      <MobileSidebar />

      {/* Desktop Sidebar */}
      <aside className={cn('sidebar hidden lg:flex', collapsed && 'collapsed')}>
        <div className="sidebar-header flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img 
                src={logoSrc}
                alt="AlbMove" 
                className="h-10 w-auto"
              />
            </div>
          )}
          {collapsed && (
            <div className="w-10 h-10 rounded-xl overflow-hidden mx-auto cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img 
                src="/favicon-sidebar.png"
                alt="AlbMove" 
                className="h-10 w-10 object-contain"
              />
            </div>
          )}
          {/* Toggle Button - next to logo */}
          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent/10"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="toggle-sidebar-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Toggle button when collapsed - below logo */}
        {collapsed && (
          <div className="px-2 py-1">
            <Button
              variant="ghost"
              size="icon"
              className="w-full h-8 text-muted-foreground hover:text-foreground hover:bg-accent/10"
              onClick={() => setCollapsed(!collapsed)}
              data-testid="toggle-sidebar-btn-collapsed"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        <nav className="sidebar-nav flex-1">
          {renderNavItems()}
        </nav>

        {isBusiness && (
          <div className={cn('px-3 pb-3', collapsed && 'px-2')}>
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="text-xs text-zinc-300">
                {!collapsed ? 'Balanca në dispozicion' : 'Balanca'}
              </p>
              <p className={cn('font-semibold text-emerald-400', collapsed ? 'text-xs' : 'text-sm')}>
                {formatCurrency(businessBalance || 0)}
              </p>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export const MobileMenuButton = ({ onClick }) => (
  <Button
    variant="ghost"
    size="icon"
    className="lg:hidden"
    onClick={onClick}
    data-testid="mobile-menu-btn"
  >
    <Menu className="w-6 h-6" />
  </Button>
);
